import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Building2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react'
import api from '@/admin/lib/api'
import { setAuthToken } from '@/shared/lib/auth-token'
import { headlessLogout } from '@/shared/lib/auth-api'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { useSession } from '@/admin/hooks/use-session'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useTranslation } from 'react-i18next'
import type {
  InvitationDetailsResponse,
  BlockedInvitationResponse,
} from '@/admin/types/organisations'

import { toastApiError } from '@/shared/lib/api-error'

export function OrganisationInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: session, isLoading: sessionLoading } = useSession()
  const [details, setDetails] = useState<InvitationDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Once we've kicked off the SSO redirect we don't want React to re-render
  // anything (the page is about to unload). Hold a ref so an extra effect
  // re-run doesn't trigger a second navigation.
  const ssoRedirectFired = useRef(false)

  // Account setup state (used only in password entry mode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [signingOut, setSigningOut] = useState(false)

  // ── Fetch invitation details and dispatch routing decision ────────
  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await api.get(
          `/organisations/invitations/details/?token=${token}`,
        )
        const data = res.data as InvitationDetailsResponse
        setDetails(data)
      } catch {
        setError('Unable to load invitation details. The link may be invalid.')
      } finally {
        setLoading(false)
      }
    }
    if (token) fetchDetails()
  }, [token])

  // ── SSO redirect: backend has handed us an opaque URL; navigate ──
  // Fires only when the user is NOT already authenticated. If they are
  // authenticated the existing accept-authenticated flow handles them.
  useEffect(() => {
    if (sessionLoading) return
    if (ssoRedirectFired.current) return
    if (!details || details.entry_mode !== 'sso') return
    if (session?.is_authenticated) return
    ssoRedirectFired.current = true
    window.location.href = buildApiUrl(details.sso_redirect_url)
  }, [details, session?.is_authenticated, sessionLoading])

  // Pre-fill the email when in password mode
  useEffect(() => {
    if (details?.entry_mode === 'password' && details.invitation.email && !email) {
      setEmail(details.invitation.email)
    }
  }, [details, email])

  async function handleAcceptAuthenticated() {
    setAccepting(true)
    try {
      await api.post('/organisations/invitations/accept/', { token })
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      toast.success('Invitation accepted! Welcome to the organisation.')
      navigate('/dashboard')
    } catch (err) {
      toastApiError(err, 'Failed to accept the invitation.')
    } finally {
      setAccepting(false)
    }
  }

  async function handleSignOutAndContinue() {
    setSigningOut(true)
    try {
      await headlessLogout()
      sessionStorage.removeItem('activeOrganisationUuid')
      sessionStorage.removeItem('activePartnerUuid')
      // Stay on this page so the user can re-authenticate as the
      // invited address. Clearing the cache forces useSession to
      // refetch with is_authenticated: false.
      queryClient.clear()
    } catch (err) {
      toastApiError(err, 'Failed to sign out.')
    } finally {
      setSigningOut(false)
    }
  }

  async function handleAcceptWithAccount() {
    setFormError(null)

    if (!email.trim() || !email.includes('@')) {
      setFormError('Please enter a valid email address.')
      return
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.')
      return
    }
    if (!termsAccepted) {
      setFormError(
        t('invite.acceptTermsError', {
          defaultValue: 'Please accept the Platform Service Terms to continue.',
        }),
      )
      return
    }

    setAccepting(true)
    try {
      const inv =
        details?.entry_mode === 'password' ? details.invitation : null
      const res = await api.post('/organisations/invitations/accept/', {
        token,
        email: email.trim(),
        password,
        first_name: inv?.first_name || '',
        last_name: inv?.last_name || '',
      })
      // The accept endpoint establishes the session and returns its opaque
      // token. Persist it before navigating - the SPA authenticates by header,
      // so without this the invitee lands on the dashboard unauthenticated and
      // is bounced straight back to a sign-in form.
      const payload = res.data as { session_token?: string; tenant_slug?: string }
      if (payload?.session_token) setAuthToken(payload.session_token)
      if (payload?.tenant_slug) sessionStorage.setItem('tenantSlug', payload.tenant_slug)
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      await queryClient.invalidateQueries({ queryKey: ['org-context'] })
      toast.success('Invitation accepted - welcome aboard!')
      navigate('/dashboard')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to accept the invitation.'
      setFormError(message)
    } finally {
      setAccepting(false)
    }
  }

  function handleSignIn() {
    const params = new URLSearchParams()
    params.set('next', `/organization-invite/${token}`)
    if (email) params.set('email', email)
    navigate(`/login?${params.toString()}`)
  }

  // ── Render helpers ────────────────────────────────────────────────

  const isAuthenticated = session?.is_authenticated
  const sessionEmail = session?.user?.email ?? ''
  const inviteEmail =
    details?.entry_mode === 'password' ? details.invitation.email : ''
  const emailMismatch =
    isAuthenticated &&
    !!sessionEmail &&
    !!inviteEmail &&
    sessionEmail.trim().toLowerCase() !== inviteEmail.trim().toLowerCase()
  const orgName =
    details?.entry_mode === 'sso'
      ? details.invitation.organisation_name
      : details?.entry_mode === 'password'
        ? details.invitation.organisation_name
        : details?.entry_mode === 'blocked'
          ? details.invitation.organisation_name
          : ''
  const role =
    details?.entry_mode === 'sso'
      ? details.invitation.role
      : details?.entry_mode === 'password'
        ? details.invitation.role
        : undefined
  const firstName =
    details?.entry_mode === 'sso'
      ? details.invitation.first_name
      : details?.entry_mode === 'password'
        ? details.invitation.first_name
        : undefined
  const lastName =
    details?.entry_mode === 'sso'
      ? details.invitation.last_name
      : details?.entry_mode === 'password'
        ? details.invitation.last_name
        : undefined

  // SSO redirect in flight: show a non-clickable loader. We deliberately
  // do NOT render the password form here - that's the SOC 2-relevant
  // invariant (password path closed for SSO tenants).
  const ssoRedirecting =
    details?.entry_mode === 'sso' && !isAuthenticated && !sessionLoading

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-md rounded-[8px] border-[0.5px] border-[var(--rule-strong)] p-8">
        {loading || sessionLoading || ssoRedirecting ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <XCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-error)]" />
            <p className="text-[13px] text-[var(--ink)]">{error}</p>
          </div>
        ) : !details ? (
          <div className="text-center py-8">
            <p className="clara-small">Invitation not found.</p>
          </div>
        ) : details.entry_mode === 'blocked' ? (
          <BlockedView details={details} onGoHome={() => navigate('/dashboard')} />
        ) : (
          <>
            {/* Invitation details (shared between authenticated and password
                paths; SSO path doesn't render this - it redirects). */}
            <div className="text-center mb-6">
              <Building2 className="mx-auto mb-4 h-8 w-8 text-[var(--green)]" />
              <h2 className="mb-1 text-[15px] text-[var(--ink)]">
                Organisation Invitation
              </h2>
              <p className="clara-small">
                You have been invited to join an organisation.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {orgName && (
                <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                  <p className="clara-label mb-1">Organisation</p>
                  <p className="text-[13px] text-[var(--ink)]">{orgName}</p>
                </div>
              )}

              {role && (
                <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                  <p className="clara-label mb-1">Role</p>
                  <p className="text-[13px] text-[var(--ink)]">{role}</p>
                </div>
              )}

              {inviteEmail && (
                <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                  <p className="clara-label mb-1">Invited As</p>
                  <p className="text-[13px] text-[var(--ink)]">{inviteEmail}</p>
                  {(firstName || lastName) && (
                    <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                      {firstName} {lastName}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            {isAuthenticated && emailMismatch ? (
              <div className="space-y-3">
                <div className="rounded-[8px] border-[0.5px] border-[var(--status-error)] bg-[var(--bg-subtle)] p-4">
                  <p className="text-[12px] text-[var(--ink)]">
                    You are signed in as{' '}
                    <span className="text-[var(--ink)]">{sessionEmail}</span>, but
                    this invitation is for{' '}
                    <span className="text-[var(--ink)]">{inviteEmail}</span>. Sign
                    out and sign in as {inviteEmail} to accept.
                  </p>
                </div>
                <Button
                  onClick={handleSignOutAndContinue}
                  disabled={signingOut}
                  className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
                >
                  {signingOut && (
                    <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />
                  )}
                  Sign out and continue
                </Button>
              </div>
            ) : isAuthenticated ? (
              <Button
                onClick={handleAcceptAuthenticated}
                disabled={accepting}
                className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
              >
                {accepting && (
                  <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />
                )}
                Accept Invitation
              </Button>
            ) : details.entry_mode === 'password' ? (
              <div className="space-y-4">
                <div className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
                  <p className="clara-small text-center mb-4">
                    Create your account to get started.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <Label className="clara-label block mb-2">Email</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3.5 py-2.5 text-[13px] text-[var(--ink)]"
                      />
                    </div>

                    <div>
                      <Label className="clara-label block mb-2">
                        Choose a password
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3.5 py-2.5 pr-10 text-[13px] text-[var(--ink)]"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAcceptWithAccount()
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[var(--ink-3)]"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {formError && (
                    <p className="mt-2 text-[11px] text-[var(--status-error)]">
                      {formError}
                    </p>
                  )}

                  <Label className="mt-4 flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(!!checked)}
                      className="h-4 w-4 rounded-[2px] border accent-[var(--green)]"
                    />
                    <span className="text-[11px] text-[var(--ink-3)]">
                      {t('invite.agreeToTerms', { defaultValue: 'I agree to the' })}{' '}
                      <a
                        href="https://iwarranty.co/platformserviceterms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--green)] underline transition-colors"
                      >
                        {t('invite.platformServiceTerms', {
                          defaultValue: 'Platform Service Terms',
                        })}
                      </a>
                      <span
                        aria-hidden="true"
                        className="ml-[2px] text-[var(--status-error)]"
                      >
                        *
                      </span>
                    </span>
                  </Label>

                  <Button
                    onClick={handleAcceptWithAccount}
                    disabled={accepting || !termsAccepted}
                    className="mt-5 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
                  >
                    {accepting && (
                      <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />
                    )}
                    Accept Invitation
                  </Button>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleSignIn}
                    className="cursor-pointer text-[11px] text-[var(--green)] underline"
                  >
                    Already have an account? Sign in
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

// ── Blocked-state subview (expired / cancelled / accepted) ──────────

function BlockedView({
  details,
  onGoHome,
}: {
  details: BlockedInvitationResponse
  onGoHome: () => void
}) {
  const { reason, invitation } = details
  if (reason === 'accepted') {
    return (
      <div className="text-center py-8">
        <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
        <h2 className="mb-2 text-[15px] text-[var(--ink)]">
          Invitation Already Accepted
        </h2>
        <p className="clara-small">
          You have already joined {invitation.organisation_name}. Sign in to
          continue.
        </p>
        <Button
          onClick={onGoHome}
          className="mt-6 cursor-pointer rounded-[4px] bg-[var(--green)] px-6 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]"
        >
          Go to Dashboard
        </Button>
      </div>
    )
  }

  const title =
    reason === 'cancelled' ? 'Invitation Cancelled' : 'Invitation Expired'
  const body =
    reason === 'cancelled'
      ? `${invitation.organisation_name} cancelled this invitation. Please ask the organisation administrator to send a new one if you should still have access.`
      : `This invitation to ${invitation.organisation_name} has expired. Please ask the organisation administrator to send a new one.`

  return (
    <div className="text-center py-8">
      <XCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-error)]" />
      <h2 className="mb-2 text-[15px] text-[var(--ink)]">{title}</h2>
      <p className="clara-small">{body}</p>
    </div>
  )
}
